<template>
  <!-- prettier-ignore -->
  <div
    v-if="firstPerson"
    class="PackagePreview"
    :class="{
      once: $props.once,
      pointable: available
    }"
    @click="onCardClicked"
  >
    <div
      v-if="showIllustration"
      class="package-illustration"
      :alt="$props.item.name"
      :style="`background-image: url(${$props.item.image})`"
    />
    <div class="content">
      <div>
        <basic-link
          class="arrow"
          target="_blank"
        >
          <template #trailing>
            <the-icon
              art="solid"
              name="arrow-right-long"
            />
          </template>
        </basic-link>
        <h2
          :class="{ 'not-available': !available }"
          v-text="$props.item.name"
        />
      </div>
      <div
        v-if="available"
        class="available"
      >
        <p
          class="intro"
          v-text="$props.item.intro"
        />
        <ul class="bullets">
          <li
            v-for="(bullet, index) in $props.item.bulletPoints"
            :key="index"
            v-text="bullet"
          />
        </ul>
        <div class="price">
          <span v-text="`${price} ${$t('product.currency')}`" />
          <the-icon
            v-if="$props.showInfoIcon"
            v-tooltip="offerText"
            art="regular"
            name="circle-info"
          />
          <p
            v-else
            class="offer"
            v-text="offerText"
          />
        </div>
      </div>
      <div
        v-else
        class="not-available"
      >
        <h3 v-text="getLabel('preview_notavailable_title')" />
        <p v-text="getLabel('preview_notavailable_text')" />
      </div>
      <basic-button
        :class="{
          'appearance-extended': isDarkTheme
        }"
        class="moreinfo-button"
        hierarchy="secondary"
        :label="getLabel('preview_button_moreinfo')"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import { currency } from '@/modules/PartnerWidget/helpers/formatters'

import usePartnerWidget from '@/hooks/usePartnerWidget'

import BasicButton from '@/components/Basic/Button'
import BasicLink from '@/components/Basic/Link'

// HOOKS
const {
  getLabel,
  getOfferText,
  getPackageTotalPrice,
  isPackageAvailable,
  partnerWidgetDisplayMode,
  partnerWidgetTheme,
} = usePartnerWidget()

// INIT
const emit = defineEmits(['clicked'])
const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  once: {
    type: Boolean,
    default: false,
  },
  showInfoIcon: {
    type: Boolean,
    default: false,
  },
})

// COMPUTED
const available = computed(() => {
  return isPackageAvailable(props.item)
})

const firstPerson = computed(() => {
  return props.item.basket?.persons[0]
})

const offerText = computed(() => {
  return getOfferText(props.item.basket.persons, props.item.showExampleOfferText)
})

const price = computed(() => {
  if (!available.value) return ''
  return currency(getPackageTotalPrice(props.item.products, firstPerson.value))
})

const showIllustration = computed(() => {
  return partnerWidgetDisplayMode.value === 'wide'
})

const isDarkTheme = computed(() => {
  return partnerWidgetTheme.value === 'darkgreen'
})

// METHODS
function onCardClicked() {
  emit('clicked')
}
</script>

<style scoped>
.PackagePreview {
  background-color: var(--on-primary);
  border-radius: var(--fixed-border-radius-fix-03);
  box-shadow: var(--elevation-level-2);
  color: var(--on-surface);
  position: relative;

  &:hover {
    color: var(--Interaction-States-hovered-default-a);

    .arrow,
    .not-available,
    .not-available h3 {
      cursor: pointer;
      color: var(--Interaction-States-hovered-default-a);
    }
  }

  &.pointable {
    cursor: pointer;
  }

  .arrow {
    position: absolute;
    right: 12px;
    top: 15px;
    font-size: var(--font-size-light-xl);
    font-weight: var(--font-weight-light);
    color: var(--Interaction-States-enabled-default);
  }

  &.once {
    .package-illustration {
      border-radius: var(--fixed-border-radius-fix-03) var(--fixed-border-radius-fix-03) 0 0;
      height: 150px;
      background-position: initial;
    }
  }

  h2 {
    font-size: var(--font-size-bold-xl);
    font-weight: var(--font-weight-bold);
    line-height: var(--line-height-bold-xl);
    margin: var(--fixed-spacing-fix-00);

    &.not-available {
      margin-bottom: var(--fixed-spacing-fix-02);
      color: var(--on-surface-low);
    }
  }

  p {
    margin: var(--fixed-spacing-fix-00);
  }

  .price {
    font-weight: var(--font-weight-regular);
    font-size: var(--font-size-regular-md);
    line-height: var(--line-height-regular-md);

    svg {
      padding-left: 5px;
    }

    p {
      margin: var(--fixed-spacing-fix-00);
    }
  }

  .moreinfo-button {
    width: 100%;
  }

  div.not-available {
    color: var(--on-surface-low);
    flex: 1 0 auto;

    h3 {
      margin: 0;
      font-size: var(--font-size-regular-xl);
      font-weight: var(--font-weight-regular);
      line-height: var(--line-height-regular-xl);
      letter-spacing: var(--letter-spacing-regular-xl);
      padding: var(--fixed-spacing-fix-02) 0;
    }

    .narrow & {
      p {
        font-size: var(--font-size-regular-sm);
        font-weight: var(--font-weight-regular);
        line-height: var(--line-height-regular-sm);
        letter-spacing: var(--letter-spacing-regular-sm);
      }
    }

    .wide & {
      p {
        font-size: var(--font-size-regular-md);
        font-weight: var(--font-weight-regular);
        line-height: var(--line-height-regular-md);
        letter-spacing: var(--letter-spacing-regular-md);
      }
    }
  }

  /* type dependant stylings */
  .narrow & {
    padding: var(--fixed-spacing-fix-03);

    .content {
      font-size: var(--font-size-regular-sm);

      .available {
        .intro {
          padding-top: var(--fixed-spacing-fix-02);
          padding-bottom: var(--fixed-spacing-fix-02);
          letter-spacing: var(--letter-spacing-regular-sm);
          line-height: var(--line-height-regular-sm);
        }
      }

      .moreinfo-button {
        display: none;
      }

      .bullets {
        display: none;
      }

      .offer {
        font-size: var(--font-size-regular-xs);
        font-weight: var(--font-weight-regular);
        line-height: var(--line-height-regular-xs);
        letter-spacing: var(--letter-spacing-regular-xs);
      }
    }
  }

  .wide &,
  .short & {
    .package-illustration {
      max-width: 100%;
      height: 150px;
      display: block;
      border-radius: var(--fixed-border-radius-fix-03) var(--fixed-border-radius-fix-03) 0 0;
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      -ms-interpolation-mode: bicubic;
      -webkit-background-size: cover;
      -moz-background-size: cover;
      -o-background-size: cover;
    }

    .content {
      display: flex;
      padding: var(--fixed-spacing-fix-04);
      flex-direction: column;
      flex: 1 0 auto;
      align-items: flex-start;
      align-self: stretch;

      .arrow {
        display: none;
      }

      .available {
        display: flex;
        flex-direction: column;
        flex: 1 0 auto;
        font-weight: var(--font-weight-regular);
        font-size: var(--font-size-regular-md);
        line-height: var(--line-height-regular-md);
        letter-spacing: var(--letter-spacing-light-md);
        margin: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-00);

        .bullets {
          flex: 1 0 auto;
          margin: var(--fixed-spacing-fix-00);
          padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-00) var(--fixed-spacing-fix-02)
            var(--fixed-spacing-fix-07);
        }
      }

      .offer {
        font-weight: var(--font-weight-regular);
        font-size: var(--font-size-regular-xs);
        line-height: var(--line-height-regular-xs);
        letter-spacing: var(--letter-spacing-regular-xs);
        margin: var(--fixed-spacing-fix-00);
      }
    }
  }
}
</style>
<style name="short" scoped>
.PackagePreview {
  .short & {
    .intro {
      display: none;
    }
  }
}
</style>

<style name="theme-darkgreen" scoped>
:root .theme-darkgreen {
  .arrow {
    color: var(--tertiary);
  }
}
</style>
